import { ChevronDown, Search } from "lucide-react";
import { useState } from "react";

import { Lado } from "@/components/revision/lado";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ===========================================================================
// LAS CUATRO PARTES DE UN EXPEDIENTE.
//
// Contabilidad las nombró así, y en este orden:
//
//   1. la boleta, nota de crédito o factura
//   2. la ficha del local comercial comprado
//   3. los vouchers de los pagos realizados
//   4. los documentos de la persona
//
// La pantalla no las tenía: colgaba las once imágenes del expediente en una
// rejilla, todas del mismo tamaño y con su clase escrita en letra pequeña
// debajo. Con cuatro DNI, tres logos, la ficha y UN voucher dentro, eso no es
// un expediente: es un montón. Lo dijo así —«veo que está anexando un montón
// de imágenes, y la verdad no va».
//
// Lo que cambia es el agrupamiento, no lo que se guarda: cada imagen ya trae su
// clase desde la lectura. Aquí solo se ponen en su cajón, y el cajón de lo que
// nadie clasificó va al final y plegado, porque es donde no hay nada que ver.
// ===========================================================================

export type Imagen = {
  recorte: string;
  pagina: number | null;
  clase: string | null;
  medio: string | null;
  importe: number | null;
  operacion: string | null;
  verificada: boolean;
  moneda: string | null;
  fechaPago: string | null;
  hora: string | null;
  banco: string | null;
  cuentaDestino: string | null;
  pagador: string | null;
  beneficiario: string | null;
  leidaPor: string | null;
  tipoDeImagen: string | null;
  cuentaDelGrupo: {
    banco: string;
    numero: string;
    moneda: string;
    razonSocial: string | null;
  } | null;
  /** La clase dice una cosa y los datos leídos dicen otra. */
  claseDudosa: boolean;
  /** Qué pago de la imagen es, y cuántos trae. */
  pagoN?: number;
  dePagos?: number | null;
  /** El movimiento del banco al que está atado ESTE pago. */
  enlace?: {
    movimientoId: string;
    fecha: string;
    banco: string;
    importe: number;
    moneda: string;
    operacion: string | null;
  } | null;
  /** «Ya lo miré y no está en ningún extracto cargado». */
  sinAbono?: boolean;
};

/**
 * CUÁNTOS PAGOS DEL EXPEDIENTE ESTÁN YA ATADOS A UN MOVIMIENTO.
 *
 * La ficha enseñaba los vouchers y no decía cuáles estaban conciliados, así que
 * con un expediente de dos o tres pagos no había forma de ver de un vistazo si
 * faltaba alguno — y falta uno es exactamente lo que deja una boleta cobrada a
 * medias.
 */
export function comoVanLosPagos(imagenes: Imagen[]): {
  atados: number;
  resueltos: number;
  total: number;
} {
  const pagos = imagenes.filter((i) => i.clase === "voucher" || i.claseDudosa);
  return {
    atados: pagos.filter((i) => i.enlace).length,
    // Los que alguien ya miró y decidió que no están en ningún extracto
    // cuentan como resueltos: si no, el expediente no se cierra nunca.
    resueltos: pagos.filter((i) => i.enlace || i.sinAbono).length,
    total: pagos.length,
  };
}

/**
 * EL PAGO DEL EXPEDIENTE: la imagen que lleva el dinero.
 *
 * Es la que va arriba del todo y suelta, porque es la que se viene a mirar. El
 * orden de preferencia es el de la evidencia: primero la que la lectura llamó
 * voucher; si ninguna lo es, la que la clase contradice —un voucher mal
 * clasificado sigue siendo el pago—; y en último lugar cualquiera con importe.
 *
 * `null` cuando el expediente no trae ningún pago legible, que también hay que
 * poder decirlo.
 */
export function pagoDelExpediente(imagenes: Imagen[]): Imagen | null {
  const conImporte = (a: Imagen, b: Imagen) =>
    Number(b.importe !== null) - Number(a.importe !== null);
  const vouchers = imagenes.filter((i) => i.clase === "voucher");
  if (vouchers.length > 0) return [...vouchers].sort(conImporte)[0] ?? null;
  const dudosas = imagenes.filter((i) => i.claseDudosa);
  if (dudosas.length > 0) return [...dudosas].sort(conImporte)[0] ?? null;
  return imagenes.find((i) => i.importe !== null) ?? null;
}

/**
 * Si de esta imagen se sacó algo que valga la pena enseñar entero.
 *
 * UN DNI NO LLEVA CUADRO, aunque el óptico le haya sacado un número. Ese
 * número es el del propio documento, y rotularlo «N.º operación» —que es como
 * se llama la columna— convierte un dato correcto en uno que miente. Solo se
 * abre el cuadro de un documento de identidad cuando la clase es dudosa, que
 * es justamente cuando hay que mirarlo.
 */
function tieneDatos(i: Imagen): boolean {
  if (i.clase === "identidad" && !i.claseDudosa) return false;
  return Boolean(
    i.importe !== null ||
      i.banco ||
      i.cuentaDestino ||
      i.fechaPago ||
      i.pagador ||
      i.beneficiario ||
      (i.operacion && i.clase !== "identidad"),
  );
}

/**
 * La barra de «cuánto de este expediente ya está atado».
 *
 * Con un solo voucher sobraba mirarlo; con dos o tres —y hay expedientes de
 * S/ 1.000 pagados con un depósito de 700 y otro de 300— hacía falta decir
 * cuántos faltan sin obligar a contar las tarjetas de abajo una por una.
 */
function BarraDePagos({ imagenes }: { imagenes: Imagen[] }) {
  const { atados, resueltos, total } = comoVanLosPagos(imagenes);
  // Con un pago la barra no aporta: la tarjeta ya lo dice todo.
  if (total < 2) return null;
  const pct = Math.round((resueltos / total) * 100);
  const todo = resueltos === total;

  return (
    <div className="mb-3 rounded-lg border bg-muted/30 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
        <span className="font-medium">
          {resueltos} de {total} pagos resueltos
        </span>
        {atados !== resueltos ? (
          <span className="text-muted-foreground">
            ({atados} atados a un movimiento
            {resueltos - atados > 0
              ? `, ${resueltos - atados} sin abono en el extracto`
              : ""}
            )
          </span>
        ) : null}
        <span
          className={cn(
            "ml-auto font-semibold tabular-nums",
            todo
              ? "text-emerald-700 dark:text-emerald-400"
              : "text-amber-700 dark:text-amber-500",
          )}
        >
          {pct}%
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full transition-[width]",
            todo ? "bg-emerald-600" : "bg-amber-500",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {!todo ? (
        <p className="mt-1.5 text-[11.5px] text-muted-foreground">
          Mientras falte uno, la boleta queda cobrada a medias aunque los demás
          estén conciliados.
        </p>
      ) : null}
    </div>
  );
}

/** La ficha del cliente, que es una HOJA del expediente y no un recorte. */
export type FichaDelCliente = {
  pagina: number | null;
  nombre: string | null;
  documento: string | null;
  telefonos: string[];
  email: string | null;
  direccion: string | null;
  distrito: string | null;
  ocupacion: string | null;
};

function DatoFicha({ k, v }: { k: string; v: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{k}</dt>
      <dd className="truncate text-right font-medium" title={v ?? ""}>
        {v || "—"}
      </dd>
    </div>
  );
}

export type OtroComprobante = {
  serieNumero: string | null;
  tipo: string;
  moneda: string;
  total: number | null;
  estado: string;
  anulaA: string | null;
  pagina: number | null;
};

const dinero = (c: number | null) =>
  c === null
    ? "—"
    : (c / 100).toLocaleString("es-PE", { minimumFractionDigits: 2 });

/**
 * El pago, arriba del todo y en su propio recuadro.
 *
 * Estaba dentro del cajón de su clase —«La ficha del local 2», y dentro el
 * cuadro—, y no se entendía: hay que bajar por dos cabeceras de sección para
 * llegar a lo único que se viene a ver. Contabilidad lo pidió al revés: «lo que
 * se leyó de la imagen debe estar al inicio, como un recuadro aparte, y de ahí
 * se abre la otra sección».
 */
export function ElPagoDelExpediente({
  imagen,
  boleta,
  cliente,
  alAbrirImagen,
  alBuscar,
  panel,
}: {
  imagen: Imagen;
  boleta: string | null;
  cliente: string | null;
  alAbrirImagen: (i: Imagen) => void;
  alBuscar?: () => void;
  panel?: React.ReactNode;
}) {
  return (
    <div>
      <div className="grid gap-2 sm:grid-cols-[200px_1fr]">
        <button
          type="button"
          onClick={() => alAbrirImagen(imagen)}
          className="block w-full cursor-zoom-in rounded border p-1.5"
        >
          <img
            src={`/imagen/${imagen.recorte}`}
            alt={imagen.clase ?? "el pago"}
            className="h-40 w-full rounded bg-muted object-contain"
          />
        </button>
        <LoQueSeLeyo
          i={imagen}
          boleta={boleta}
          cliente={cliente}
          alBuscar={alBuscar}
        />
      </div>
      {panel}
    </div>
  );
}

export function SeccionesDelExpediente({
  imagenes,
  comprobante,
  otrosComprobantes,
  alAbrirImagen,
  alAbrirHoja,
  alBuscarMovimiento,
  buscando,
  panel,
  yaArriba,
  archivoId,
  ficha,
}: {
  imagenes: Imagen[];
  comprobante: {
    serieNumero: string | null;
    cliente: string | null;
    total: number | null;
    moneda: string | null;
    pagina: number | null;
  };
  otrosComprobantes: OtroComprobante[];
  alAbrirImagen: (i: Imagen) => void;
  alAbrirHoja: (pagina: number) => void;
  /** Buscar en el extracto el abono que corresponde a este voucher. */
  alBuscarMovimiento?: (i: Imagen) => void;
  /** Qué imagen tiene la búsqueda abierta, y el panel que la resuelve. */
  buscando?: Imagen | null;
  panel?: React.ReactNode;
  /**
   * La que ya se enseñó arriba, en su propio recuadro. Aquí sale como
   * miniatura y sin repetir su cuadro: verlo dos veces en la misma pantalla
   * hace dudar de si son dos pagos.
   */
  yaArriba?: Imagen | null;
  /** Para dibujar la hoja de la ficha sin abrir el visor. */
  archivoId: string;
  /** Lo que se leyó de la ficha. `null` si el expediente no la trae. */
  ficha?: FichaDelCliente | null;
}) {
  const de = (clase: string) => imagenes.filter((i) => i.clase === clase);
  const vouchers = de("voucher");
  const identidad = de("identidad");
  // LOS RECORTES SE DEDUPLICAN POR IMAGEN. La nota legal del pie sale una vez
  // por hoja en la que aparece: en un expediente de cinco hojas, la misma tira
  // se enseñaba cuatro veces seguidas como si fueran cuatro fichas distintas.
  const fichas = Array.from(
    new Map(de("ficha").map((i) => [i.recorte, i])).values(),
  );
  const resto = imagenes.filter(
    (i) => !["voucher", "identidad", "ficha"].includes(i.clase ?? ""),
  );
  // Lo que toda imagen necesita para enseñar su cuadro: de qué venta es y cómo
  // buscar su abono. Viaja junto para no arrastrar cinco props por cada nivel.
  const ctx = {
    boleta: comprobante.serieNumero,
    cliente: comprobante.cliente,
    alBuscarMovimiento,
    buscando,
    panel,
    yaArriba: yaArriba ?? null,
  };

  return (
    <div className="space-y-3">
      {/* 1 · EL COMPROBANTE. No es una imagen sino una hoja del PDF, así que
          lleva a la hoja en vez de a un recorte. */}
      <Seccion
        titulo="El comprobante"
        cuantos={1 + otrosComprobantes.length}
        subtitulo="la boleta, la nota de crédito o la factura"
      >
        <ul className="space-y-1 text-[12.5px]">
          <FilaComprobante
            serie={comprobante.serieNumero}
            tipo="boleta"
            moneda={comprobante.moneda ?? "PEN"}
            total={comprobante.total}
            pagina={comprobante.pagina}
            principal
            alAbrirHoja={alAbrirHoja}
          />
          {otrosComprobantes.map((c) => (
            <FilaComprobante
              key={c.serieNumero}
              serie={c.serieNumero}
              tipo={c.tipo}
              moneda={c.moneda}
              total={c.total}
              pagina={c.pagina}
              anulaA={c.anulaA}
              anulado={c.estado === "anulado"}
              alAbrirHoja={alAbrirHoja}
            />
          ))}
        </ul>
      </Seccion>

      {/* 2 · LA FICHA, que es una HOJA y no un montón de recortes.
          Aquí se enseñaban los trozos que el clasificador marcó como `ficha`, y
          en un expediente real eso eran cuatro copias de la nota legal del pie
          —«las separaciones tienen una vigencia»—. Esa etiqueta está bien
          puesta y hace falta: impide que el cronograma de cuotas se lea como un
          pago. Lo que estaba mal era enseñarla en el sitio de la ficha. */}
      <Seccion
        titulo="La ficha del local"
        cuantos={ficha ? 1 : 0}
        subtitulo="los datos del comprador, tal como los leyó la hoja"
        vacia="Este expediente no trae ficha del cliente, o el lector no la reconoció."
      >
        {ficha ? (
          <div className="flex flex-wrap items-start gap-3">
            {ficha.pagina ? (
              <button
                type="button"
                onClick={() => alAbrirHoja(ficha.pagina ?? 1)}
                className="shrink-0 cursor-zoom-in"
                aria-label={`Ver la hoja ${ficha.pagina} en grande`}
              >
                <img
                  src={`/pagina/${archivoId}/${ficha.pagina}`}
                  alt=""
                  loading="lazy"
                  className="h-32 w-24 rounded border bg-muted object-cover object-top"
                />
              </button>
            ) : null}

            <div className="min-w-[15rem] flex-1">
              <dl className="grid gap-x-6 gap-y-1 text-[12.5px] sm:grid-cols-2">
                <DatoFicha k="Nombre" v={ficha.nombre} />
                <DatoFicha k="Documento" v={ficha.documento} />
                <DatoFicha
                  k={ficha.telefonos.length > 1 ? "Teléfonos" : "Teléfono"}
                  v={ficha.telefonos.join(" · ") || null}
                />
                <DatoFicha k="Distrito" v={ficha.distrito} />
                <DatoFicha k="Dirección" v={ficha.direccion} />
                <DatoFicha k="Ocupación" v={ficha.ocupacion} />
                <DatoFicha k="Correo" v={ficha.email} />
              </dl>
              {ficha.pagina ? (
                <button
                  type="button"
                  onClick={() => alAbrirHoja(ficha.pagina ?? 1)}
                  className="mt-2 text-[12px] text-cyan-700 underline-offset-2 hover:underline dark:text-cyan-400"
                >
                  Ver la hoja {ficha.pagina} del PDF
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Y los trozos que el lector marcó, que son parte de la hoja y no la
            hoja: van debajo, en pequeño y dichos por lo que son. */}
        {fichas.length > 0 ? (
          <details className="mt-3">
            <summary className="cursor-pointer text-[11.5px] text-muted-foreground">
              {fichas.length === 1
                ? "1 trozo de estas hojas que el lector marcó como parte de la ficha"
                : `${fichas.length} trozos de estas hojas que el lector marcó como parte de la ficha`}
            </summary>
            <div className="mt-2">
              <Rejilla imagenes={fichas} alAbrir={alAbrirImagen} ctx={ctx} />
            </div>
          </details>
        ) : null}
      </Seccion>

      {/* 3 · LOS VOUCHERS. Los únicos que llevan importe y número de
          operación, que es con lo que se concilia. */}
      <Seccion
        titulo="Los vouchers del pago"
        cuantos={vouchers.length}
        subtitulo="es con lo que se cruza contra el extracto"
        vacia="Ninguna imagen se reconoció como voucher. Si el expediente trae uno, puede estar entre las que el modelo no ha leído."
        destacar
      >
        <BarraDePagos imagenes={imagenes} />
        <Rejilla imagenes={vouchers} alAbrir={alAbrirImagen} ctx={ctx} />
      </Seccion>

      {/* 4 · LOS DOCUMENTOS DE LA PERSONA. */}
      <Seccion
        titulo="Documentos de la persona"
        cuantos={identidad.length}
        subtitulo="DNI y demás documentos de identidad"
        vacia="Ninguna imagen se reconoció como documento de identidad."
      >
        <Rejilla imagenes={identidad} alAbrir={alAbrirImagen} ctx={ctx} />
      </Seccion>

      {/* Y lo que no cayó en ningún cajón: logos, hojas en blanco, y lo que el
          óptico no supo reconocer. Va al final y PLEGADO — es el montón que
          hacía ilegible la pantalla— pero no se esconde: un voucher que el
          óptico no reconoció está justo aquí. */}
      {resto.length > 0 ? (
        <Seccion
          titulo="Lo demás"
          cuantos={resto.length}
          subtitulo="logos, hojas en blanco y lo que el óptico no supo reconocer"
          plegadaPorDefecto
        >
          <Rejilla imagenes={resto} alAbrir={alAbrirImagen} ctx={ctx} />
        </Seccion>
      ) : null}
    </div>
  );
}

function FilaComprobante({
  serie,
  tipo,
  moneda,
  total,
  pagina,
  anulaA,
  anulado,
  principal,
  alAbrirHoja,
}: {
  serie: string | null;
  tipo: string;
  moneda: string;
  total: number | null;
  pagina: number | null;
  anulaA?: string | null;
  anulado?: boolean;
  principal?: boolean;
  alAbrirHoja: (pagina: number) => void;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <b className="font-medium">{serie ?? "—"}</b>
      <span className="text-muted-foreground">
        {tipo === "nota_credito"
          ? "nota de crédito"
          : tipo === "nota_debito"
            ? "nota de débito"
            : tipo}
      </span>
      <span className="tabular-nums">
        {moneda === "USD" ? "US$" : "S/"} {dinero(total)}
      </span>
      {principal ? (
        <span className="rounded border px-1.5 text-[11px] text-muted-foreground">
          la del expediente
        </span>
      ) : null}
      {anulaA ? (
        <span className="text-muted-foreground">anula la {anulaA}</span>
      ) : null}
      {anulado ? (
        <span className="rounded border border-amber-600/40 px-1.5 text-[11px] text-amber-700 dark:text-amber-500">
          anulada
        </span>
      ) : null}
      {pagina ? (
        <button
          type="button"
          onClick={() => alAbrirHoja(pagina)}
          className="ml-auto text-[11.5px] underline underline-offset-2 hover:no-underline"
        >
          hoja {pagina}
        </button>
      ) : null}
    </li>
  );
}

function Seccion({
  titulo,
  subtitulo,
  cuantos,
  vacia,
  destacar,
  plegadaPorDefecto,
  children,
}: {
  titulo: string;
  subtitulo: string;
  cuantos: number;
  /** Qué decir cuando no hay nada. Sin esto, un cajón vacío no se explica. */
  vacia?: string;
  /** La sección que sostiene la conciliación: se ve antes que las demás. */
  destacar?: boolean;
  plegadaPorDefecto?: boolean;
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(!plegadaPorDefecto);
  return (
    <section
      className={cn(
        "overflow-hidden rounded-lg border",
        destacar && cuantos > 0 && "border-cyan-600/40",
      )}
    >
      <button
        type="button"
        onClick={() => setAbierta((x) => !x)}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            !abierta && "-rotate-90",
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-[12.5px]">
            {titulo}{" "}
            <span
              className={cn(
                "tabular-nums",
                cuantos === 0 ? "text-muted-foreground" : "",
              )}
            >
              {cuantos}
            </span>
          </span>
          <span className="block text-[11px] text-muted-foreground">
            {subtitulo}
          </span>
        </span>
      </button>
      {abierta ? (
        <div className="border-t px-3 py-2.5">
          {cuantos === 0 && vacia ? (
            <p className="text-[11.5px] text-muted-foreground">{vacia}</p>
          ) : (
            children
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Lo que se sacó de una imagen, en el MISMO cuadro que la revisión guiada.
 *
 * Pedido tal cual: «que muestre la info extraída en un componente tal cual como
 * dice revisión guiada». Y hacía falta: el dato leído estaba en la base y la
 * ficha solo enseñaba la miniatura y la clase. Se vio con un expediente donde
 * el óptico clasificó un voucher como «ficha» y AUN ASÍ le sacó la moneda, el
 * banco y el número de operación — la pantalla decía «ficha» y no había forma
 * de enterarse de que dentro había un pago leído.
 */
function LoQueSeLeyo({
  i,
  boleta,
  cliente,
  alBuscar,
}: {
  i: Imagen;
  /** La boleta y el cliente del expediente: el pago es de esa venta. */
  boleta: string | null;
  cliente: string | null;
  /** Buscar el abono del banco que le corresponde. */
  alBuscar?: () => void;
}) {
  const filas: string[][] = [];
  if (i.fechaPago)
    filas.push(["Cuándo", i.hora ? `${i.fechaPago} · ${i.hora}` : i.fechaPago]);
  if (i.operacion) filas.push(["N.º operación", i.operacion]);
  if (i.medio) filas.push(["Cómo se pagó", i.medio]);
  if (i.banco) filas.push(["Banco del voucher", i.banco]);
  if (i.tipoDeImagen) filas.push(["Tipo de imagen", i.tipoDeImagen]);
  if (i.pagador) filas.push(["Lo pagó", i.pagador]);
  if (i.beneficiario) filas.push(["A nombre de", i.beneficiario]);
  if (i.cuentaDestino) filas.push(["Cuenta de destino", i.cuentaDestino]);
  // La boleta y el cliente son del expediente, no de la imagen: se enseñan
  // igual porque la pregunta que se hace aquí es «¿de qué venta es este pago?».
  if (boleta) filas.push(["Boleta", boleta]);
  if (cliente) filas.push(["Cliente", cliente]);
  filas.push(["Hoja del PDF", String(i.pagina ?? "?")]);
  // Quién la leyó cambia cuánto fiarse: el óptico es local y gratis, y es el
  // que confunde un voucher con una ficha.
  filas.push([
    "La leyó",
    i.leidaPor === null || i.leidaPor === "optico"
      ? "el lector óptico"
      : i.leidaPor,
  ]);

  return (
    <div className="space-y-2">
      <Lado
        titulo="Lo que se leyó de esta imagen"
        importe={
          i.importe === null
            ? "sin importe"
            : `${i.moneda === "USD" ? "US$" : "S/"} ${dinero(i.importe)}`
        }
        filas={filas}
        etiqueta={i.medio}
        origen={i.tipoDeImagen}
        nota={
          i.claseDudosa
            ? {
                texto: `Se clasificó como «${i.clase}» y trae datos de pago. Conviene mirarla.`,
                tono: "warn",
              }
            : i.verificada
              ? { texto: "Una persona ya verificó esta lectura.", tono: "ok" }
              : null
        }
        // A qué cuenta del grupo entró el dinero. Va debajo y no como una fila
        // más porque no es un dato del papel: es lo que se sabe DESPUÉS de
        // cotejar la cuenta de destino contra el maestro.
        cuenta={
          i.cuentaDelGrupo
            ? {
                texto: `Cuenta ${i.cuentaDelGrupo.banco} ${i.cuentaDelGrupo.numero} de ${i.cuentaDelGrupo.razonSocial ?? "una empresa del grupo"}`,
                tono: "ok",
              }
            : i.cuentaDestino
              ? {
                  texto:
                    "Esa cuenta de destino no está en el maestro del grupo.",
                  tono: "warn",
                }
              : null
        }
      />
      {alBuscar ? (
        <Button size="sm" variant="outline" onClick={alBuscar}>
          <Search className="mr-1.5 size-3.5" />
          Buscar el movimiento en el banco
        </Button>
      ) : null}
    </div>
  );
}

type Contexto = {
  boleta: string | null;
  cliente: string | null;
  alBuscarMovimiento?: (i: Imagen) => void;
  /** La imagen cuya búsqueda está abierta, para pintarla debajo. */
  buscando?: Imagen | null;
  panel?: React.ReactNode;
  yaArriba: Imagen | null;
};

function Rejilla({
  imagenes,
  alAbrir,
  ctx,
}: {
  imagenes: Imagen[];
  alAbrir: (i: Imagen) => void;
  ctx: Contexto;
}) {
  if (imagenes.length === 0) return null;
  // Con datos leídos, cada imagen ocupa su propia fila y lleva su cuadro al
  // lado: apretar un cuadro de nueve campos en una celda de rejilla lo vuelve
  // ilegible, que es de donde se viene.
  const conCuadro = imagenes.filter(
    (i) => tieneDatos(i) && i.recorte !== ctx.yaArriba?.recorte,
  );
  const sinCuadro = imagenes.filter(
    (i) => !tieneDatos(i) || i.recorte === ctx.yaArriba?.recorte,
  );
  return (
    <>
      {conCuadro.length > 0 ? (
        <div className="mb-2 space-y-2">
          {conCuadro.map((i) => (
            <div
              key={i.recorte}
              className="grid gap-2 sm:grid-cols-[200px_1fr]"
            >
              <button
                type="button"
                onClick={() => alAbrir(i)}
                className="block w-full cursor-zoom-in rounded border p-1.5"
              >
                <img
                  src={`/imagen/${i.recorte}`}
                  alt={i.clase ?? "adjunto"}
                  className="h-40 w-full rounded bg-muted object-contain"
                />
              </button>
              <div>
                <LoQueSeLeyo
                  i={i}
                  boleta={ctx.boleta}
                  cliente={ctx.cliente}
                  alBuscar={
                    ctx.alBuscarMovimiento
                      ? () => ctx.alBuscarMovimiento?.(i)
                      : undefined
                  }
                />
                {/* La búsqueda se abre DEBAJO del cuadro de su imagen y no en
                    un diálogo: lo que se compara —importe, fecha, operación—
                    tiene que poder mirarse a la vez que la lista. */}
                {ctx.buscando?.recorte === i.recorte ? ctx.panel : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <RejillaSimple imagenes={sinCuadro} alAbrir={alAbrir} />
    </>
  );
}

function RejillaSimple({
  imagenes,
  alAbrir,
}: {
  imagenes: Imagen[];
  alAbrir: (i: Imagen) => void;
}) {
  if (imagenes.length === 0) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {imagenes.map((i) => (
        <div key={i.recorte} className="rounded border p-1.5">
          <button
            type="button"
            onClick={() => alAbrir(i)}
            className="block w-full cursor-zoom-in"
          >
            <img
              src={`/imagen/${i.recorte}`}
              alt={i.clase ?? "adjunto"}
              className="h-24 w-full rounded bg-muted object-contain"
            />
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-muted-foreground">
            <span>hoja {i.pagina ?? "?"}</span>
            {i.medio ? (
              <span className="rounded border px-1">{i.medio}</span>
            ) : null}
            {i.verificada ? (
              <span className="rounded bg-emerald-600/15 px-1 text-emerald-700 dark:text-emerald-400">
                verificada
              </span>
            ) : null}
          </div>
          {i.claseDudosa ? (
            <p className="mt-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10.5px] text-amber-800 dark:text-amber-300">
              se clasificó como «{i.clase}» y trae datos de pago
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}
